import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { trackWishlistEvent } from '@theme/wishlist-track';
import { productEventProps } from '@theme/klaviyo-client';
import { showSignInPrompt } from '@theme/wishlist-prompt';
import { announce } from '@theme/ca-announcer';
import {
  isLoggedIn,
  isSaved,
  isProductSaved,
  savedVariantIds,
  totalCount,
  toggle,
  toggleGuest,
  mergeGuestOnLogin,
  markLoggedOut,
  pruneSavedKey,
  DEFAULT_LIST_ID,
  wishlistText,
} from '@theme/wishlist';

const SHEET_WAIT = 3000;

const mergeTracked = new Set();

class WishlistHeart extends Component {
  requiredRefs = ['wishlistButton'];

  connectedCallback() {
    super.connectedCallback();
    this.render();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.setAttribute('data-wl-ready', ''));
    });
    this.onExternalUpdate = (event) => {
      const target = event.detail?.productId;
      if (target === undefined || Number(target) === this.productId) this.render();

      const merged = event.detail?.mergedItems;
      const mine = merged?.find(
        (item) => Number(item.pid) === this.productId && Number(item.vid) === this.variantId
      );
      // One event per merged item, not per heart: a product shown in both the grid and a
      // recommendations rail would otherwise double-count in Klaviyo.
      if (mine) {
        const key = `${mine.pid}:${mine.vid ?? ''}`;
        if (!mergeTracked.has(key)) {
          mergeTracked.add(key);
          this.track('Added to Wishlist');
        }
      }
    };
    document.addEventListener(ThemeEvents.wishlistUpdate, this.onExternalUpdate);

    this.onVariantUpdate = (event) => {
      const owner = event.detail?.data?.productId;
      if (owner !== undefined && Number(owner) !== this.productId) return;

      const variant = event.detail?.resource ?? event.detail?.variant;
      if (!variant?.id) return;
      const newProduct = event.detail?.data?.newProduct;
      if (newProduct?.id) {
        this.dataset.productId = String(newProduct.id);
        if (newProduct.handle) this.dataset.productHandle = String(newProduct.handle);
      }
      this.dataset.variantId = String(variant.id);
      if (Number.isFinite(variant.price)) this.dataset.price = String(variant.price);
      if (variant.title != null) this.dataset.variantTitle = String(variant.title);
      if (variant.sku != null) this.dataset.sku = String(variant.sku);
      if (variant.available != null) this.dataset.available = String(variant.available);
      this.dataset.compareAt = String(variant.compare_at_price ?? 0);
      this.render();
    };
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.onExternalUpdate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  get productId() {
    return Number(this.dataset.productId);
  }

  get variantId() {
    return this.dataset.variantId ? Number(this.dataset.variantId) : null;
  }

  get listId() {
    return this.dataset.listId || DEFAULT_LIST_ID;
  }

  get handle() {
    return this.dataset.productHandle || '';
  }

  get variantsNode() {
    return this.querySelector(':scope > script[data-wishlist-variants]');
  }

  get variantData() {
    const node = this.variantsNode;
    if (!node) return null;
    try {
      const data = JSON.parse(node.textContent);
      return Array.isArray(data?.v) && data.v.length > 1 ? data : null;
    } catch {
      return null;
    }
  }

  async openSharedSheet(button) {
    const shared = document.querySelector('wishlist-variant-sheet');
    if (!shared || !this.variantData) return false;
    if (typeof shared.openFor !== 'function') {
      await Promise.race([
        customElements.whenDefined('wishlist-variant-sheet'),
        new Promise((resolve) => setTimeout(resolve, SHEET_WAIT)),
      ]);
    }
    return typeof shared.openFor === 'function' && shared.openFor(this, button);
  }

  get soleAvailableVariant() {
    const available = this.variantData?.v.filter((variant) => variant.a === 1) ?? [];
    return available.length === 1 && available[0].id === this.variantId;
  }

  visitProduct() {
    const { productUrl, productHandle } = this.dataset;
    const url = productUrl || (productHandle && `${window.Shopify?.routes?.root || '/'}products/${productHandle}`);
    if (url) window.location.assign(url);
  }

  render() {
    const { wishlistButton: button } = this.refs;
    if (!button) return;

    const chooser = Boolean(this.variantData);
    const saved = chooser
      ? isProductSaved(this.productId, this.listId)
      : isSaved(this.productId, this.variantId, this.listId);
    button.setAttribute('aria-pressed', String(saved));

    if (chooser) {
      button.setAttribute('aria-haspopup', 'dialog');
      if (!button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', 'false');
      const template = wishlistText(saved ? 'labelChooseSaved' : 'labelChoose');
      const title = this.dataset.labelTitle || this.dataset.productTitle || '';
      if (template) button.setAttribute('aria-label', template.replace('%title%', () => title));
    }

    const { wishlistLabel } = this.refs;
    if (wishlistLabel) {
      wishlistLabel.textContent = saved
        ? wishlistLabel.dataset.labelActive
        : wishlistLabel.dataset.labelIdle;
    }
  }

  async toggleSaved() {
    const { wishlistButton: button } = this.refs;
    if (!button) return;

    if (this.variantsNode) {
      if (await this.openSharedSheet(button)) return;
      if (!this.soleAvailableVariant) {
        this.visitProduct();
        return;
      }
    }

    if (!isLoggedIn()) {
      const { inList, stored, full } = toggleGuest(this.productId, this.variantId, this.handle, {
        price: this.price,
        source: this.source,
      });
      this.render();
      if (full) this.reportError('list_full');
      else if (!stored) this.reportError('storage_unavailable');
      else if (inList) this.promptSignIn();
      return;
    }

    const previous = button.getAttribute('aria-pressed') === 'true';
    button.setAttribute('aria-pressed', String(!previous));
    button.setAttribute('data-busy', '');
    button.setAttribute('aria-busy', 'true');

    try {
      const result = await toggle(this.productId, this.variantId, this.listId, this.handle, {
        price: this.price,
        source: this.source,
      });
      this.track(result.in_list ? 'Added to Wishlist' : 'Removed from Wishlist');
    } catch (error) {
      button.setAttribute('aria-pressed', String(previous));

      if (error.code === 'login_required') {
        markLoggedOut();
        if (previous) {
          // A remove that never reached the server: drop the cached key so the heart can clear.
          pruneSavedKey(this.productId, this.variantId);
        } else {
          toggleGuest(this.productId, this.variantId, this.handle, {
            price: this.price,
            source: this.source,
          });
          this.promptSignIn();
        }
        this.render();
        return;
      }
      this.reportError(error.code);
    } finally {
      button.removeAttribute('data-busy');
      button.removeAttribute('aria-busy');
      this.render();
    }
  }

  promptSignIn() {
    showSignInPrompt();
  }

  reportError(code) {
    const full = code === 'list_full' || code === 'wishlist_too_large';
    announce(wishlistText(full ? 'errorFull' : 'errorGeneric'));
  }

  get price() {
    const cents = Number.parseInt(this.dataset.price ?? '', 10);
    return Number.isFinite(cents) ? cents : null;
  }

  get source() {
    return this.dataset.source || 'other';
  }

  klaviyoProperties(metric) {
    return productEventProps({
      productId: this.productId,
      variantId: this.variantId,
      productName: this.dataset.productTitle || '',
      variantName: this.dataset.variantTitle || '',
      sku: this.dataset.sku || '',
      vendor: this.dataset.vendor || '',
      productType: this.dataset.productType || '',
      image: this.dataset.image,
      url: this.dataset.productUrl,
      price: this.price,
      compareAt: Number.parseInt(this.dataset.compareAt ?? '', 10) || 0,
      currency: this.dataset.currency || '',
      available: this.dataset.available === 'true',
      source: this.source,
    });
  }

  track(metric) {
    trackWishlistEvent(metric, this.klaviyoProperties(metric));
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.render();
    this.onWishlistUpdate = () => this.render();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.onWishlistUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.onWishlistUpdate);
  }

  render() {
    const count = totalCount();
    const slot = this.querySelector('[data-wishlist-count-text]');
    if (slot) slot.textContent = count > 0 ? String(count) : '';
    else this.textContent = count > 0 ? String(count) : '';
    this.toggleAttribute('hidden', count === 0);

    const described = document.getElementById(this.dataset.describes || '');
    if (!described) return;

    if (count === 0) {
      described.textContent = '';
    } else if (count === 1) {
      described.textContent = this.dataset.countOne || '';
    } else {
      described.textContent = (this.dataset.countOther || '').replace('%d', count);
    }
  }
}

if (!customElements.get('wishlist-heart')) {
  customElements.define('wishlist-heart', WishlistHeart);
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}

/**
 * Open a product page on the variant the shopper saved.
 *
 * Without this the page opens on the product's default variant, so a shopper who saved a
 * different one arrives at an unfilled heart. An explicit ?variant= always wins.
 */
async function preselectSavedVariant() {
  if (new URL(window.location.href).searchParams.has('variant')) return;

  const heart = document.querySelector('.ca-buybox-actions wishlist-heart[data-product-id]');
  const picker = document.querySelector('variant-picker');
  if (!heart || !picker) return;

  const saved = savedVariantIds(Number(heart.dataset.productId));
  if (!saved.size) return;

  await customElements.whenDefined('variant-picker');

  if (picker.querySelectorAll('fieldset').length > 1) return;

  const inputs = [...picker.querySelectorAll('input[data-variant-id]')];
  if (inputs.some((input) => input.checked && saved.has(input.dataset.variantId))) return;

  // savedVariantIds preserves store order, which is most-recent-first for a guest.
  for (const variantId of saved) {
    const target = inputs.find((input) => input.dataset.variantId === variantId);
    if (target && target.dataset.optionAvailable !== 'false' && target.getAttribute('aria-disabled') !== 'true') {
      target.click();
      return;
    }
  }
}

mergeGuestOnLogin();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void preselectSavedVariant(), { once: true });
} else {
  void preselectSavedVariant();
}
